import type { UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";

import type { IQuery } from "@citrine/client";
import ClientBase, { useRevertableQuery } from "@citrine/client";

import type {
  PredictorEvaluationExecution,
  PredictorEvaluationExecutionResults,
} from "./predictor-evaluation-execution";

interface PredictorEvaluationExecutionQuery extends IQuery {
  page?: number;
  per_page?: number;
  predictor_id?: string;
  predictor_version?: string;
}

export class PredictorEvaluationExecutionClient extends ClientBase<
  PredictorEvaluationExecution,
  string,
  PredictorEvaluationExecutionQuery
> {
  protected constructor(private projectId: string) {
    super({
      endpoint: `/api/v1/projects/${projectId}/predictor-evaluation-executions`,
      findField: "response",
      defaultQuery: {
        page: 1,
        per_page: 20,
        predictor_id: undefined,
        predictor_version: undefined,
      },
    });
  }

  static getInstance(projectId: string) {
    return new this(projectId);
  }

  getResults(
    id: string,
    evaluator_name: string,
    allow_downsampling: boolean,
    downsampling_limit: number,
    downsampling_random_seed?: number,
  ): Promise<PredictorEvaluationExecutionResults> {
    return this.request({
      url: `${this.endpoint}/${id}/results`,
      params: {
        evaluator_name,
        allow_downsampling,
        downsampling_max: allow_downsampling ? downsampling_limit : undefined,
        downsampling_random_seed,
      },
    });
  }

  useGetResults({
    id,
    evaluator_name,
    allow_downsampling,
    downsampling_limit,
    downsampling_random_seed,
    options = {},
  }: {
    id?: string;
    evaluator_name?: string;
    allow_downsampling: boolean;
    downsampling_limit: number;
    downsampling_random_seed?: number;
    options: Omit<
      UseQueryOptions<PredictorEvaluationExecutionResults>,
      "queryKey" | "queryFn"
    >;
  }): UseQueryResult<PredictorEvaluationExecutionResults> {
    const queryKey = [...this.getModelCacheKey(id), "results"]
      .concat(evaluator_name || [])
      .concat([
        allow_downsampling,
        downsampling_limit,
        downsampling_random_seed ?? 0,
      ]);
    return useRevertableQuery({
      ...options,
      queryKey,
      queryFn: () =>
        this.getResults(
          id!,
          evaluator_name!,
          allow_downsampling,
          downsampling_limit,
          downsampling_random_seed,
        ),

      enabled: Boolean(id && evaluator_name && (options.enabled ?? true)),
    });
  }

  protected createConfig(
    execution: Partial<PredictorEvaluationExecution>,
  ): AxiosRequestConfig {
    return {
      method: "POST",
      url: `/api/v1/projects/${this.projectId}/predictor-evaluation-workflows/${execution.workflow_id}/executions`,
      data: { module_uid: execution.predictor_id },
    };
  }
}
